import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaBookOpen, FaCalendarAlt, FaCheck, FaDownload, FaEdit, FaEye, FaFileAlt,
    FaArchive, FaListUl, FaPlus, FaTasks,
} from "react-icons/fa";
import { COURSES_CONSTANTS } from "../_MODULES/_API";
import { COURSE_TABS } from "../_MODULES/_Navigation";
import { CourseFormModal } from "../_UI/CourseFormModal";
import { CourseItemModal } from "../_UI/CourseItemModal";
import { CourseReadModal } from "../_UI/CourseReadModal";
import { PageHeader, PageHeaderMeta } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Badge, EmptyState, Spinner } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { ViewPagedSection } from "../../../../_UI/ViewPagedSection";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { downloadCourseMarkdown, downloadCoursePdf } from "../_UI/courseExport.helpers";
import { usePagedList } from "../../../../_UI/usePagedList";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { REACT_HOT_TOAST_ERROR } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";
import "../_UI/_Styles/Courses.css";

const TAB_ICONS = {
    notebook: <FaBookOpen />,
    tasks: <FaTasks />,
    schedule: <FaCalendarAlt />,
    info: <FaListUl />,
};
const TABS = COURSE_TABS.map((item) => ({ ...item, icon: TAB_ICONS[item.value] }));
const TAB_VALUES = new Set(COURSE_TABS.map((item) => item.value));

const COURSE_STATUS = {
    IDEA: "Idea",
    DRAFT: "Borrador",
    PLANNING: "Planificando",
    READY: "Preparado",
    ACTIVE: "En curso",
    PAUSED: "Pausado",
    COMPLETED: "Finalizado",
};

const NOTE_TYPE = {
    TOPIC: "Tema",
    NOTE: "Apunte",
    SUMMARY: "Resumen",
    EXAMPLE: "Ejemplo",
    EXERCISE: "Ejercicio",
    QUESTION: "Pregunta",
    RESOURCE: "Recurso",
    IDEA: "Idea",
    CHECKLIST: "Checklist",
    REFERENCE: "Referencia",
    OTHER: "Otro",
    CLASS: "Tema",
    VIDEO: "Recurso",
    LIVE_CLASS: "Clase",
    PRACTICE: "Practica",
    PROJECT: "Proyecto",
    READING: "Lectura",
    QUIZ: "Pregunta",
    EXAM: "Evaluacion",
    DEMO: "Ejemplo",
    LAB: "Practica",
};

const NOTE_STATUS = {
    TO_PREPARE: "Por preparar",
    DRAFT: "Borrador",
    READY: "Listo",
    TAUGHT: "Ya ensenado",
    REVIEW: "Revisar",
    PLANNED: "Por preparar",
    IN_PROGRESS: "En preparacion",
    RECORDED: "Listo",
    PUBLISHED: "Listo",
    COMPLETED: "Ya ensenado",
    ON_HOLD: "Revisar",
};

const TASK_TYPE = {
    PREPARE_CLASS: "Preparar clase",
    REVIEW_TOPIC: "Revisar tema",
    CREATE_EXAMPLE: "Crear ejemplo",
    CREATE_EXERCISE: "Crear ejercicio",
    RESEARCH: "Investigar",
    MATERIAL: "Material",
    FOLLOW_UP: "Seguimiento",
    ADMIN: "Administrativo",
    OTHER: "Otro",
    PREPARATION: "Preparar clase",
    CONTENT: "Contenido",
    RECORDING: "Material",
    EDITING: "Revision",
    REVIEW: "Revisar tema",
    PUBLISHING: "Material",
    STUDENT_SUPPORT: "Seguimiento",
    MARKETING: "Otro",
};

const SCHEDULE_TYPE = {
    CLASS: "Clase",
    STUDY: "Estudio / repaso",
    PREPARATION: "Preparacion",
    REVIEW: "Revision",
    DEADLINE: "Fecha limite",
    MEETING: "Reunion",
    OTHER: "Otro",
    LIVE_CLASS: "Clase",
    RECORDING: "Preparacion",
    OFFICE_HOURS: "Consultas",
    EXAM: "Evaluacion",
    RELEASE: "Entrega / publicacion",
};

const taskTone = { TODO: "neutral", IN_PROGRESS: "warning", BLOCKED: "danger", DONE: "success", CANCELLED: "neutral" };
const noteTone = { TO_PREPARE: "neutral", DRAFT: "warning", READY: "success", TAUGHT: "success", REVIEW: "warning" };

const formatDateTime = (value) => value
    ? new Date(value).toLocaleString("es-AR", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    })
    : "Sin fecha";

const excerpt = (item) => {
    const text = String(item.description || item.content || item.instructorNotes || "").replace(/\s+/g, " ").trim();
    return text ? (text.length > 150 ? `${text.slice(0, 150)}...` : text) : "Sin resumen todavia.";
};

export const CourseDetailViewMain = () => {
    const { id, tab: routeTab } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const tab = TAB_VALUES.has(routeTab) ? routeTab : "notebook";
    const setTab = (value) => navigate(`/courses/${id}/${value}`);
    const linkedTabs = TABS.map((item) => ({ ...item, to: `/courses/${id}/${item.value}` }));
    const [modal, setModal] = useState(null);
    const [reader, setReader] = useState(null);
    const [editCourse, setEditCourse] = useState(false);
    const [notebookSearch, setNotebookSearch] = useState("");
    const [taskSearch, setTaskSearch] = useState("");
    const [scheduleSearch, setScheduleSearch] = useState("");

    const { apiGet, useApiData, useApiLoading } = useApiQuery({
        minTimeQuery: 150,
        enablePagination: false,
        startLoading: true,
    });

    const { actionMutate } = useApiActionMutation({
        isToastError: true,
        isToastSuccess: true,
        minTimeMutate: 0,
    });

    useEffect(() => {
        apiGet({ endpoint: COURSES_CONSTANTS.DETAIL, isDetail: true, config: { pathParams: { id } } });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (useApiData?.id) setCourse(useApiData);
    }, [useApiData]);

    const modules = course?.modules || [];
    const tasks = course?.tasks || [];
    const schedule = course?.schedule || [];
    const notes = useMemo(
        () => modules.flatMap((module) => (module.lessons || []).map((item) => ({
            ...item,
            moduleTitle: module.title,
            moduleDescription: module.description,
            moduleObjectives: module.objectives,
        }))),
        [modules],
    );

    const MODULE_BY_ID = useMemo(
        () => new Map(modules.map((module) => [module.id, module])),
        [modules],
    );

    const FILTERED_MODULES = useMemo(() => modules.filter((module) => {
        const MATCHES_MODULE = onMatchesSearch(notebookSearch, [
            module.title, module.description, module.objectives, module.status,
        ]);

        const MATCHES_LESSON = (module.lessons || []).some((item) => onMatchesSearch(notebookSearch, [
            item.title, item.description, item.content, item.instructorNotes, item.resourceUrl,
            item.type, NOTE_TYPE[item.type], item.status, NOTE_STATUS[item.status],
            module.title, module.description, module.objectives,
        ]));

        return MATCHES_MODULE || MATCHES_LESSON;
    }), [modules, notebookSearch]);

    const FILTERED_NOTES = useMemo(() => notes.filter((item) => onMatchesSearch(notebookSearch, [
        item.title, item.description, item.content, item.instructorNotes, item.resourceUrl,
        item.type, NOTE_TYPE[item.type], item.status, NOTE_STATUS[item.status],
        item.moduleTitle, item.moduleDescription, item.moduleObjectives,
    ])), [notes, notebookSearch]);

    const FILTERED_TASKS = useMemo(() => tasks.filter((task) => {
        const MODULE = MODULE_BY_ID.get(task.moduleId);
        return onMatchesSearch(taskSearch, [
            task.title, task.description, task.notes, task.type, TASK_TYPE[task.type],
            task.priority, task.status, task.dueDate, MODULE?.title, MODULE?.description,
        ]);
    }), [tasks, taskSearch, MODULE_BY_ID]);

    const FILTERED_SCHEDULE = useMemo(() => schedule.filter((event) => {
        const MODULE = MODULE_BY_ID.get(event.moduleId);
        return onMatchesSearch(scheduleSearch, [
            event.title, event.description, event.notes, event.location, event.meetingUrl,
            event.type, SCHEDULE_TYPE[event.type], event.status, event.scheduledAt,
            MODULE?.title, MODULE?.description,
        ]);
    }), [schedule, scheduleSearch, MODULE_BY_ID]);

    // El buscador filtra antes del paginado: nunca paginamos resultados que ya no coinciden.
    const MODULE_PAGER = usePagedList(FILTERED_MODULES, 6, "modulesPage");
    const NOTE_PAGER = usePagedList(FILTERED_NOTES, 6, "notesPage");
    const TASK_PAGER = usePagedList(FILTERED_TASKS, 6, "tasksPage");
    const SCHEDULE_PAGER = usePagedList(FILTERED_SCHEDULE, 6, "schedulePage");

    const NOTE_PAGE_IDS = useMemo(
        () => new Set(NOTE_PAGER.paged.map((item) => item.id)),
        [NOTE_PAGER.paged],
    );

    const NOTEBOOK_MODULES = useMemo(() => {
        if (FILTERED_NOTES.length === 0) return MODULE_PAGER.paged;

        return FILTERED_MODULES.filter((module) => {
            const moduleNotes = module.lessons || [];

            // Un modulo vacio sigue visible en la primera pagina para que se pueda completar.
            if (moduleNotes.length === 0) return NOTE_PAGER.usePage === 1;

            return moduleNotes.some((item) => NOTE_PAGE_IDS.has(item.id));
        });
    }, [FILTERED_MODULES, FILTERED_NOTES.length, NOTE_PAGE_IDS, NOTE_PAGER.usePage, MODULE_PAGER.paged]);

    const NOTEBOOK_PAGER = FILTERED_NOTES.length > 0 ? NOTE_PAGER : MODULE_PAGER;

    const saveCourse = async (values) => {
        const previous = { ...course };
        setCourse((current) => ({ ...current, ...values }));
        const response = await actionMutate({
            method: "PATCH",
            endpoint: COURSES_CONSTANTS.UPDATE,
            id,
            data: values,
            successMessage: "Curso actualizado",
        });
        if (!response.success) {
            setCourse(previous);
            return response;
        }
        const saved = response?.data?.data || response?.data;
        if (saved?.id) setCourse((current) => ({ ...current, ...saved }));
        return true;
    };

    const config = {
        module: {
            create: COURSES_CONSTANTS.MODULE_CREATE,
            update: COURSES_CONSTANTS.MODULE_UPDATE,
            remove: COURSES_CONSTANTS.MODULE_REMOVE,
            archive: COURSES_CONSTANTS.MODULE_ARCHIVE,
        },
        lesson: {
            create: COURSES_CONSTANTS.LESSON_CREATE,
            update: COURSES_CONSTANTS.LESSON_UPDATE,
            remove: COURSES_CONSTANTS.LESSON_REMOVE,
            archive: COURSES_CONSTANTS.LESSON_ARCHIVE,
        },
        task: {
            create: COURSES_CONSTANTS.TASK_CREATE,
            update: COURSES_CONSTANTS.TASK_UPDATE,
            remove: COURSES_CONSTANTS.TASK_REMOVE,
            archive: COURSES_CONSTANTS.TASK_ARCHIVE,
        },
        schedule: {
            create: COURSES_CONSTANTS.SCHEDULE_CREATE,
            update: COURSES_CONSTANTS.SCHEDULE_UPDATE,
            remove: COURSES_CONSTANTS.SCHEDULE_REMOVE,
            archive: COURSES_CONSTANTS.SCHEDULE_ARCHIVE,
        },
    };

    const saveItem = async (kind, values, item) => {
        const isEdit = !!item?.id && !String(item.id).startsWith("new-");
        const targetModuleId = values.moduleId || item?.moduleId || "";
        const duplicateRows = kind === "module" ? (course.modules || [])
            : kind === "lesson" ? (course.modules || []).flatMap((module) => (module.lessons || []).map((row) => ({ ...row, moduleId: row.moduleId || module.id })))
                : kind === "task" ? (course.tasks || [])
                    : (course.schedule || []);
        if (isDuplicateRecord({
            rows: duplicateRows, value: values.title, currentId: isEdit ? item.id : null,
            getValue: (row) => row?.title,
            scope: kind === "lesson" ? targetModuleId : null,
            getScope: kind === "lesson" ? (row) => row?.moduleId || "" : null,
        })) {
            REACT_HOT_TOAST_ERROR("Ya existe un registro con ese título en este espacio");
            return false;
        }
        const snapshot = course;
        const tempId = isEdit ? item.id : `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const optimistic = { ...item, ...values, id: tempId };

        if (kind === "module") {
            setCourse((current) => ({
                ...current,
                modules: isEdit
                    ? current.modules.map((row) => row.id === item.id ? { ...row, ...values } : row)
                    : [...current.modules, { ...optimistic, lessons: [] }],
            }));
        }

        if (kind === "lesson") {
            setCourse((current) => {
                const targetId = values.moduleId || item?.moduleId;
                return {
                    ...current,
                    modules: current.modules.map((module) => {
                        const withoutCurrent = (module.lessons || []).filter((row) => row.id !== item?.id);
                        if (module.id !== targetId) return { ...module, lessons: withoutCurrent };
                        return { ...module, lessons: [...withoutCurrent, optimistic] };
                    }),
                };
            });
        }

        if (kind === "task") {
            setCourse((current) => ({
                ...current,
                tasks: isEdit
                    ? current.tasks.map((row) => row.id === item.id ? optimistic : row)
                    : [...current.tasks, optimistic],
            }));
        }

        if (kind === "schedule") {
            setCourse((current) => ({
                ...current,
                schedule: isEdit
                    ? current.schedule.map((row) => row.id === item.id ? optimistic : row)
                    : [...current.schedule, optimistic],
            }));
        }

        const response = isEdit
            ? await actionMutate({
                method: "PATCH",
                endpoint: config[kind].update,
                id: item.id,
                data: values,
                successMessage: "Guardado",
            })
            : await onApiAction({
                method: "POST",
                endpoint: config[kind].create,
                pathParams: { courseId: id },
                data: values,
                successMessage: "Agregado",
            });

        if (!response.success) {
            setCourse(snapshot);
            return response;
        }

        const saved = response?.data?.data || response?.data;
        if (!saved?.id) return true;

        if (kind === "module") {
            setCourse((current) => ({
                ...current,
                modules: current.modules.map((row) => row.id === tempId
                    ? { ...saved, lessons: row.lessons || [] }
                    : row),
            }));
        }
        if (kind === "task") {
            setCourse((current) => ({ ...current, tasks: current.tasks.map((row) => row.id === tempId ? saved : row) }));
        }
        if (kind === "schedule") {
            setCourse((current) => ({ ...current, schedule: current.schedule.map((row) => row.id === tempId ? saved : row) }));
        }
        if (kind === "lesson") {
            setCourse((current) => ({
                ...current,
                modules: current.modules.map((module) => ({
                    ...module,
                    lessons: (module.lessons || []).map((row) => row.id === tempId ? saved : row),
                })),
            }));
        }
        return true;
    };

    const archiveChild = async (kind, item) => {
        const label = kind === "lesson" ? "apunte" : kind === "module" ? "módulo" : kind === "task" ? "tarea" : "evento";
        const accepted = await confirm.danger(
            `Archivar ${label}`,
            kind === "module"
                ? `Se archiva "${item.title}" junto con sus apuntes, tareas y eventos vinculados. Podés restaurarlo desde Archivados.`
                : `Se archiva "${item.title}". Podés restaurarlo desde Archivados.`,
        );
        if (!accepted) return;

        const snapshot = course;
        if (kind === "module") setCourse((current) => ({
            ...current,
            modules: current.modules.filter((row) => row.id !== item.id),
            tasks: current.tasks.filter((row) => String(row.moduleId || "") !== String(item.id)),
            schedule: current.schedule.filter((row) => String(row.moduleId || "") !== String(item.id)),
        }));
        if (kind === "lesson") setCourse((current) => ({
            ...current,
            modules: current.modules.map((module) => ({
                ...module,
                lessons: (module.lessons || []).filter((row) => row.id !== item.id),
            })),
        }));
        if (kind === "task") setCourse((current) => ({ ...current, tasks: current.tasks.filter((row) => row.id !== item.id) }));
        if (kind === "schedule") setCourse((current) => ({ ...current, schedule: current.schedule.filter((row) => row.id !== item.id) }));

        const response = await onApiAction({
            method: "PATCH",
            endpoint: config[kind].archive,
            pathParams: { id: item.id },
            successMessage: "Archivado",
        });
        if (!response.success) setCourse(snapshot);
    };

    const toggleTask = async (task) => {
        const next = task.status === "DONE" ? "TODO" : "DONE";
        setCourse((current) => ({
            ...current,
            tasks: current.tasks.map((row) => row.id === task.id ? { ...row, status: next } : row),
        }));

        const response = await actionMutate({
            method: "PATCH",
            endpoint: COURSES_CONSTANTS.TASK_UPDATE,
            id: task.id,
            data: { status: next },
            successMessage: next === "DONE" ? "Tarea completada" : "Tarea reabierta",
        });

        if (!response.success) {
            setCourse((current) => ({
                ...current,
                tasks: current.tasks.map((row) => row.id === task.id ? task : row),
            }));
        }
    };

    if (useApiLoading && !course) return <Spinner label="Cargando curso..." />;
    if (!course) return <EmptyState icon={<FaBookOpen />} title="No encontramos ese curso" />;

    const pendingTasks = tasks.filter((task) => task.status !== "DONE" && task.status !== "CANCELLED").length;

    return (
        <ViewLayout
            header={
                <PageHeader
                    title={course.title}
                    icon={<FaBookOpen />}
                    backTo="/courses"
                    description={course.description || "Cuaderno privado para preparar y recordar lo que queres ensenar."}
                    status={<Badge tone="neutral" size="sm">{COURSE_STATUS[course.status] || course.status}</Badge>}
                    meta={
                        <PageHeaderMeta items={[
                            { label: "Modulos", value: modules.length },
                            { label: "Apuntes", value: notes.length },
                            { label: "Tareas pendientes", value: pendingTasks },
                        ]} />
                    }
                    actions={
                        <>
                            <CardActionMenu
                                title="Descargar curso"
                                ariaLabel="Opciones de descarga del curso"
                                items={[
                                    { key: "download-pdf", label: "Descargar PDF completo", icon: <FaFileAlt />, onClick: () => downloadCoursePdf(course) },
                                    { key: "download-markdown", label: "Descargar Markdown por carpetas (.zip)", icon: <FaDownload />, onClick: () => downloadCourseMarkdown(course) },
                                ]}
                            />
                            <AppButton icon={<FaEye />} variant="ghost" tone="neutral" onClick={() => setReader({ kind: "overview", id: null })}>
                                Modo lectura
                            </AppButton>
                            <AppButton icon={<FaEdit />} variant="soft" onClick={() => setEditCourse(true)}>
                                Datos del curso
                            </AppButton>
                        </>
                    }
                    tabs={linkedTabs}
                    activeTab={tab}
                    onChangeTab={setTab}
                />
            }
        >
            {tab === "notebook" && (
                <section className="LStudio__academic_panel">
                    <div className="LStudio__app_panel_bar">
                        <div className="LStudio__academic_panel_bar">
                            <div>
                                <strong className="LStudio__app_panel_bar_title">Cuaderno docente</strong>
                                <p>Organiza el temario por modulos y guarda debajo temas, resumentes, ejemplos, ejercicios e ideas.</p>
                            </div>
                            <div className="LStudio__academic_panel_tools">
                                <SearchFilter value={notebookSearch} onChange={setNotebookSearch}
                                    total={notes.length > 0 ? notes.length : modules.length}
                                    matches={FILTERED_NOTES.length > 0 ? FILTERED_NOTES.length : FILTERED_MODULES.length}
                                    placeholder="Modulo, tema, apunte, contenido o estado" />
                                <AppButton icon={<FaPlus />} onClick={() => setModal({ kind: "module", item: null })}>
                                    Nuevo modulo
                                </AppButton>
                            </div>
                        </div>
                    </div>

                    {modules.length === 0 && (
                        <EmptyState
                            icon={<FaBookOpen />}
                            title="Todavia no armaste el cuaderno"
                            description="Crea un modulo y empeza a guardar los temas o apuntes que queres ensenar."
                            action={<AppButton icon={<FaPlus />} onClick={() => setModal({ kind: "module", item: null })}>Crear modulo</AppButton>}
                        />
                    )}

                    {modules.length > 0 && FILTERED_MODULES.length === 0 && FILTERED_NOTES.length === 0 && (
                        <EmptyState icon={<FaBookOpen />} title="No encontramos contenido"
                            description="Probá con otro módulo, tema, concepto, contenido o estado." />
                    )}

                    <ViewPagedSection
                        pager={NOTEBOOK_PAGER}
                        singular={FILTERED_NOTES.length > 0 ? "apunte" : "modulo"}
                        plural={FILTERED_NOTES.length > 0 ? "apuntes" : "modulos"}
                    >
                        <div className="LStudio__academic_modules">
                        {NOTEBOOK_MODULES.map((module) => {
                            const moduleIndex = modules.findIndex((row) => row.id === module.id);
                            const visibleLessons = FILTERED_NOTES.length === 0
                                ? (module.lessons || [])
                                : (module.lessons || []).filter((item) => NOTE_PAGE_IDS.has(item.id));

                            return (
                            <article key={module.id} className="LStudio__academic_module">
                                <header className="LStudio__academic_module_head">
                                    <div className="LStudio__academic_module_identity">
                                        <span>{String(moduleIndex + 1).padStart(2, "0")}</span>
                                        <div>
                                            <strong>{module.title}</strong>
                                            <p>{module.description || "Sin descripcion."}</p>
                                        </div>
                                    </div>
                                    <div className="LStudio__academic_module_actions">
                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaEye />}
                                            onClick={() => setReader({ kind: "module", id: module.id })}>Ver</AppButton>
                                        <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaEdit />}
                                            onClick={() => setModal({ kind: "module", item: module })}>Editar</AppButton>
                                        <AppButton size="sm" variant="ghost" tone="danger" isIconOnly icon={<FaArchive />}
                                            aria-label="Archivar módulo" onClick={() => archiveChild("module", module)} />
                                        <AppButton size="sm" icon={<FaPlus />}
                                            onClick={() => setModal({ kind: "lesson", item: { moduleId: module.id } })}>Nuevo apunte</AppButton>
                                    </div>
                                </header>

                                <div className="LStudio__academic_note_head" aria-hidden="true">
                                    <span>Tipo</span>
                                    <span>Apunte / tema</span>
                                    <span>Estado</span>
                                    <span>Acciones</span>
                                </div>

                                <div className="LStudio__academic_notes">
                                    {visibleLessons.map((item) => (
                                        <div key={item.id} className="LStudio__academic_note_row"
                                            role="button" tabIndex={0}
                                            onClick={() => setReader({ kind: "lesson", id: item.id })}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") setReader({ kind: "lesson", id: item.id });
                                            }}>
                                            <Badge tone="neutral" size="sm">{NOTE_TYPE[item.type] || item.type}</Badge>
                                            <div className="LStudio__academic_note_main">
                                                <strong>{item.title}</strong>
                                                <p>{excerpt(item)}</p>
                                            </div>
                                            <Badge tone={noteTone[item.status] || "neutral"} size="sm">
                                                {NOTE_STATUS[item.status] || item.status}
                                            </Badge>
                                            <div className="LStudio__academic_row_actions" onClick={(event) => event.stopPropagation()}>
                                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEye />}
                                                    aria-label="Ver apunte" onClick={() => setReader({ kind: "lesson", id: item.id })} />
                                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEdit />}
                                                    aria-label="Editar apunte" onClick={() => setModal({ kind: "lesson", item })} />
                                                <AppButton size="sm" variant="ghost" tone="danger" isIconOnly icon={<FaArchive />}
                                                    aria-label="Archivar apunte" onClick={() => archiveChild("lesson", item)} />
                                            </div>
                                        </div>
                                    ))}

                                    {(module.lessons || []).length === 0 && (
                                        <button className="LStudio__academic_add_note" type="button"
                                            onClick={() => setModal({ kind: "lesson", item: { moduleId: module.id } })}>
                                            <FaPlus /> Agregar el primer apunte de este modulo
                                        </button>
                                    )}
                                </div>
                            </article>
                            );
                        })}
                        </div>
                    </ViewPagedSection>
                </section>
            )}

            {tab === "tasks" && (
                <section className="LStudio__academic_panel">
                    <div className="LStudio__app_panel_bar">
                        <div className="LStudio__academic_panel_bar">
                            <div>
                                <strong className="LStudio__app_panel_bar_title">Tareas personales</strong>
                                <p>Recordatorios privados para preparar clases, revisar conceptos, investigar o armar material.</p>
                            </div>
                            <div className="LStudio__academic_panel_tools">
                                <SearchFilter value={taskSearch} onChange={setTaskSearch}
                                    total={tasks.length} matches={FILTERED_TASKS.length}
                                    placeholder="Tarea, tipo, prioridad, modulo o estado" />
                                <AppButton icon={<FaPlus />} onClick={() => setModal({ kind: "task", item: null })}>Nueva tarea</AppButton>
                            </div>
                        </div>
                    </div>

                    <ViewPagedSection pager={TASK_PAGER} singular="tarea" plural="tareas">
                        <div className="LStudio__academic_task_list">
                        {TASK_PAGER.paged.map((task) => (
                            <div key={task.id} className="LStudio__academic_task_row" data-done={task.status === "DONE"}
                                role="button" tabIndex={0}
                                onClick={() => setReader({ kind: "task", id: task.id })}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") setReader({ kind: "task", id: task.id });
                                }}>
                                <button className="LStudio__academic_task_check" type="button"
                                    onClick={(event) => { event.stopPropagation(); toggleTask(task); }}>
                                    {task.status === "DONE" && <FaCheck />}
                                </button>
                                <div className="LStudio__academic_task_main">
                                    <strong>{task.title}</strong>
                                    <span>{TASK_TYPE[task.type] || task.type}{task.dueDate ? ` · ${new Date(task.dueDate).toLocaleDateString("es-AR")}` : ""}</span>
                                    {task.description && <p>{task.description}</p>}
                                </div>
                                <Badge tone={taskTone[task.status] || "neutral"} size="sm">{task.status}</Badge>
                                <div className="LStudio__academic_row_actions" onClick={(event) => event.stopPropagation()}>
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEye />}
                                        aria-label="Ver tarea" onClick={() => setReader({ kind: "task", id: task.id })} />
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEdit />}
                                        aria-label="Editar tarea" onClick={() => setModal({ kind: "task", item: task })} />
                                    <AppButton size="sm" variant="ghost" tone="danger" isIconOnly icon={<FaArchive />}
                                        aria-label="Archivar tarea" onClick={() => archiveChild("task", task)} />
                                </div>
                            </div>
                        ))}

                        {tasks.length === 0 && (
                            <EmptyState icon={<FaTasks />} title="No tenes tareas en este curso"
                                description="Anota aca lo que te falta preparar, revisar o investigar." />
                        )}

                        {tasks.length > 0 && FILTERED_TASKS.length === 0 && (
                            <EmptyState icon={<FaTasks />} title="No encontramos tareas"
                                description="Probá con otro texto, prioridad, módulo, tipo o estado." />
                        )}
                        </div>
                    </ViewPagedSection>
                </section>
            )}

            {tab === "schedule" && (
                <section className="LStudio__academic_panel">
                    <div className="LStudio__app_panel_bar">
                        <div className="LStudio__academic_panel_bar">
                            <div>
                                <strong className="LStudio__app_panel_bar_title">Itinerario personal</strong>
                                <p>Fechas de clase, preparacion, estudio, revision y cualquier hito que quieras recordar.</p>
                            </div>
                            <div className="LStudio__academic_panel_tools">
                                <SearchFilter value={scheduleSearch} onChange={setScheduleSearch}
                                    total={schedule.length} matches={FILTERED_SCHEDULE.length}
                                    placeholder="Evento, fecha, lugar, modulo o tipo" />
                                <AppButton icon={<FaPlus />} onClick={() => setModal({ kind: "schedule", item: null })}>Nuevo evento</AppButton>
                            </div>
                        </div>
                    </div>

                    <ViewPagedSection pager={SCHEDULE_PAGER} singular="evento" plural="eventos">
                        <div className="LStudio__academic_schedule_list">
                        {SCHEDULE_PAGER.paged.map((event) => (
                            <div key={event.id} className="LStudio__academic_schedule_row" role="button" tabIndex={0}
                                onClick={() => setReader({ kind: "schedule", id: event.id })}
                                onKeyDown={(keyEvent) => {
                                    if (keyEvent.key === "Enter" || keyEvent.key === " ") setReader({ kind: "schedule", id: event.id });
                                }}>
                                <div className="LStudio__academic_schedule_date">
                                    <FaCalendarAlt />
                                    <strong>{formatDateTime(event.scheduledAt)}</strong>
                                </div>
                                <div className="LStudio__academic_schedule_main">
                                    <strong>{event.title}</strong>
                                    <span>{SCHEDULE_TYPE[event.type] || event.type}{event.location ? ` · ${event.location}` : ""}</span>
                                    {event.description && <p>{event.description}</p>}
                                </div>
                                <div className="LStudio__academic_row_actions" onClick={(rowEvent) => rowEvent.stopPropagation()}>
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEye />}
                                        aria-label="Ver evento" onClick={() => setReader({ kind: "schedule", id: event.id })} />
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaEdit />}
                                        aria-label="Editar evento" onClick={() => setModal({ kind: "schedule", item: event })} />
                                    <AppButton size="sm" variant="ghost" tone="danger" isIconOnly icon={<FaArchive />}
                                        aria-label="Archivar evento" onClick={() => archiveChild("schedule", event)} />
                                </div>
                            </div>
                        ))}

                        {schedule.length === 0 && (
                            <EmptyState icon={<FaCalendarAlt />} title="Todavia no hay itinerario"
                                description="Agrega fechas que quieras tener a mano dentro de este curso." />
                        )}

                        {schedule.length > 0 && FILTERED_SCHEDULE.length === 0 && (
                            <EmptyState icon={<FaCalendarAlt />} title="No encontramos eventos"
                                description="Probá con otra fecha, lugar, módulo, tipo o texto." />
                        )}
                        </div>
                    </ViewPagedSection>
                </section>
            )}

            {tab === "info" && (
                <section className="LStudio__academic_panel">
                    <div className="LStudio__app_panel_bar">
                        <div className="LStudio__academic_panel_bar">
                            <div>
                                <strong className="LStudio__app_panel_bar_title">Ficha del curso</strong>
                                <p>Contexto privado para recordar a quien esta dirigido, objetivos y alcance.</p>
                            </div>
                            <div className="LStudio__academic_panel_actions">
                                <AppButton icon={<FaEye />} variant="ghost" tone="neutral" onClick={() => setReader({ kind: "info", id: null })}>Ver ficha</AppButton>
                                <AppButton icon={<FaEdit />} variant="soft" onClick={() => setEditCourse(true)}>Editar ficha</AppButton>
                            </div>
                        </div>
                    </div>

                    <div className="LStudio__academic_info">
                        <div><span>Descripcion</span><p>{course.description || "Sin descripcion."}</p></div>
                        <div><span>A quien va dirigido</span><p>{course.audience || "Sin definir."}</p></div>
                        <div><span>Objetivos que quiero cubrir</span><p>{course.objectives || "Sin definir."}</p></div>
                        <div><span>Conocimientos previos</span><p>{course.prerequisites || "Sin definir."}</p></div>
                        <div><span>Referencia / contexto</span><p>{course.platform || "Sin definir."}</p></div>
                        <div><span>Fechas</span><p>{course.startDate || "-"} a {course.endDate || "-"}</p></div>
                    </div>
                </section>
            )}

            <CourseReadModal
                isOpen={!!reader}
                onClose={() => setReader(null)}
                course={course}
                courseId={id}
                initialSelection={reader}
            />

            <CourseFormModal
                isOpen={editCourse}
                onClose={() => setEditCourse(false)}
                course={course}
                onSave={saveCourse}
            />

            <CourseItemModal
                kind={modal?.kind}
                item={modal?.item}
                isOpen={!!modal}
                onClose={() => setModal(null)}
                modules={modules}
                onSave={(values) => saveItem(modal.kind, values, modal.item)}
            />
        </ViewLayout>
    );
};
